import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#0088FE', '#FF8042', '#82ca9d', '#ffc658', '#d3d3d3'];

/**
 * Daily Response Rate Chart
 */
export const DailyResponseChart = ({ responsesByDate }) => {
    return (
        <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={responsesByDate}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#8884d8"
                        name="All Responses"
                    />
                    <Line
                        type="monotone"
                        dataKey="submitted"
                        stroke="#82ca9d"
                        name="Completed"
                    />
                    <Line
                        type="monotone"
                        dataKey="incomplete"
                        stroke="#ffc658"
                        name="Incomplete"
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

/**
 * Cumulative Responses Chart
 */
export const CumulativeResponseChart = ({ responsesByDate }) => {
    return (
        <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={responsesByDate}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="cumulativeTotal"
                        stroke="#8884d8"
                        name="All Responses"
                    />
                    <Line
                        type="monotone"
                        dataKey="cumulativeSubmitted"
                        stroke="#82ca9d"
                        name="Completed"
                    />
                    <Line
                        type="monotone"
                        dataKey="cumulativeIncomplete"
                        stroke="#ffc658"
                        name="Incomplete"
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

/**
 * Completion Status Pie Chart
 */
export const CompletionPieChart = ({ submittedResponses, incompleteResponses }) => {
    const data = [
        { name: 'Completed', value: submittedResponses },
        { name: 'Incomplete', value: incompleteResponses }
    ];

    return (
        <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

/**
 * Abandonment Chart
 */
export const AbandonmentChart = ({ abandonmentData }) => {
    return (
        <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={abandonmentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="questionNumber"
                        label={{ value: 'Question Number', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis
                        label={{ value: 'Number of Abandonments', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                        formatter={(value, name, props) => [value, `Question ${props.payload.questionNumber}`]}
                    />
                    <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#FF8042"
                        name="Abandonments"
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

/**
 * Invitation Status Pie Chart
 */
export const InvitationStatusPieChart = ({ completedCount, startedCount, totalInvitations }) => {
    const data = [
        { name: 'Completed', value: completedCount },
        { name: 'Started but not completed', value: startedCount - completedCount },
        { name: 'Not started', value: totalInvitations - startedCount }
    ];

    return (
        <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                    >
                        <Cell fill="#82ca9d" /> {/* Completed */}
                        <Cell fill="#ffc658" /> {/* Started but not completed */}
                        <Cell fill="#d3d3d3" /> {/* Not started */}
                    </Pie>
                    <Tooltip />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};